import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, ArrowLeft, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { toast } from "sonner";
import { useCreateProjectMutation } from "@/hooks/queries/useProjectsQuery";
import { useOrganizationListQuery } from "@/hooks/queries/useOrganizationsQuery";
import {
  ProjectStatus,
  ProjectPhase,
  Sector,
  SectorLabels,
  DealType,
} from "@/types/project";
import type { CreateProjectCommand } from "@/types/project";

const AddProject = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const createProjectMutation = useCreateProjectMutation();
  const { data: organizations = [], isLoading: organizationsLoading } = useOrganizationListQuery();

  const [formData, setFormData] = useState({
    name: "",
    targetName: "",
    status: "" as string,
    phase: "" as string,
    expectedClose: undefined as Date | undefined,
    sector: "" as string,
    dealType: "" as string,
    redacted: "",
    owner: "",
    organizationId: "",
  });

  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const command: CreateProjectCommand = {
      name: formData.name,
      targetName: formData.targetName || formData.name,
      organizationId: Number(formData.organizationId),
      status: formData.status !== "" ? Number(formData.status) as ProjectStatus : null,
      phase: formData.phase !== "" ? Number(formData.phase) as ProjectPhase : null,
      expectedClose: formData.expectedClose ? format(formData.expectedClose, "yyyy-MM-dd") : null,
      sector: formData.sector !== "" ? Number(formData.sector) as Sector : null,
      dealType: formData.dealType !== "" ? Number(formData.dealType) as DealType : null,
      redacted: formData.redacted,
      owner: formData.owner || null,
    };

    try {
      await createProjectMutation.mutateAsync(command);
      navigate("/project");
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/project")}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("createNewProject")}</h1>
            <p className="text-muted-foreground">{t("fillDetails")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-card p-6 rounded-lg border border-border">
          <div className="space-y-2">
            <Label>{t("organization")} *</Label>
            <Select
              value={formData.organizationId}
              onValueChange={(value) => setFormData({ ...formData, organizationId: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder={t("selectOrganization")} />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((organization) => (
                  <SelectItem key={organization.id} value={String(organization.id)}>
                    {organization.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {organizations.length === 0 && !organizationsLoading ? (
              <p className="text-xs text-muted-foreground">{t("createOrganizationFirst")}</p>
            ) : null}
          </div>

          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t("enterProjectName")} *</Label>
            <Input
              id="name"
              placeholder={t("enterProjectName")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          {/* Status and Phase */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("status")}</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("selectStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(ProjectStatus.Active)}>{t("active")}</SelectItem>
                  <SelectItem value={String(ProjectStatus.OnHold)}>{t("onHold")}</SelectItem>
                  <SelectItem value={String(ProjectStatus.Cancelled)}>{t("cancelled")}</SelectItem>
                  <SelectItem value={String(ProjectStatus.Done)}>{t("done")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t("phase")}</Label>
              <Select
                value={formData.phase}
                onValueChange={(value) => setFormData({ ...formData, phase: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("selectPhase")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(ProjectPhase.InitialReview)}>{t("initialReview")}</SelectItem>
                  <SelectItem value={String(ProjectPhase.DueDiligence)}>{t("dueDiligence")}</SelectItem>
                  <SelectItem value={String(ProjectPhase.Negotiation)}>Negotiation</SelectItem>
                  <SelectItem value={String(ProjectPhase.Closing)}>{t("closing")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Expected Close Date */}
          <div className="space-y-2">
            <Label>{t("expectedCloseDate")}</Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.expectedClose && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.expectedClose ? (
                    format(formData.expectedClose, "PPP")
                  ) : (
                    <span>{t("expectedCloseDate")}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.expectedClose}
                  onSelect={(date) => {
                    setFormData({ ...formData, expectedClose: date });
                    setDatePickerOpen(false);
                  }}
                  autoFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Redacted List */}
          <div className="space-y-2">
            <Label htmlFor="redacted">{t("redacted")} *</Label>
            <Textarea
              id="redacted"
              placeholder={t("redacted")}
              value={formData.redacted}
              onChange={(e) => setFormData({ ...formData, redacted: e.target.value })}
              required
            />
            <p className="text-xs text-muted-foreground">{t("redactedCommaSeparated")}</p>
          </div>

          {/* Sector */}
          <div className="space-y-2">
            <Label>{t("projectSector")}</Label>
            <Select
              value={formData.sector}
              onValueChange={(value) => setFormData({ ...formData, sector: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("selectSector")} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SectorLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Deal Type */}
          <div className="space-y-2">
            <Label>{t("projectDealType")}</Label>
            <Select
              value={formData.dealType}
              onValueChange={(value) => setFormData({ ...formData, dealType: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("selectDealType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={String(DealType.Acquisition)}>{t("dealTypeAcquisition")}</SelectItem>
                <SelectItem value={String(DealType.Merger)}>{t("dealTypeMerger")}</SelectItem>
                <SelectItem value={String(DealType.GrowthInvestment)}>{t("dealTypeGrowthInvestment")}</SelectItem>
                <SelectItem value={String(DealType.PEBuyout)}>{t("dealTypePEBuyout")}</SelectItem>
                <SelectItem value={String(DealType.CarveOut)}>{t("dealTypeCarveOut")}</SelectItem>
                <SelectItem value={String(DealType.JointVenture)}>{t("dealTypeJointVenture")}</SelectItem>
                <SelectItem value={String(DealType.AssetPurchase)}>{t("dealTypeAssetPurchase")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Owner */}
          <div className="space-y-2">
            <Label htmlFor="owner">{t("owner")}</Label>
            <Input
              id="owner"
              placeholder={t("selectOwner")}
              value={formData.owner}
              onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse gap-4 pt-4 sm:flex-row">
            <Button type="submit" className="w-full sm:flex-1" disabled={createProjectMutation.isPending || !formData.organizationId}>
              {createProjectMutation.isPending && <Loader2 className="animate-spin mr-2" size={16} />}
              {t("createProject")}
            </Button>
            <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => navigate("/project")} disabled={createProjectMutation.isPending}>
              {t("cancel")}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default AddProject;
